import React, { useMemo, useState } from 'react';
import { withStyles } from '@material-ui/core/styles/index';
import { Dialog, DialogContent, IconButton, List, ListItem, ListItemText, Typography, AppBar, Toolbar } from '@material-ui/core/index';
import CloseIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';

const styles = (theme) => ({
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  appBar: {
    position: 'sticky'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  headerTitle: {
    color: '#fff'
  },
  list: {
    maxHeight: 360,
    overflowY: 'auto'
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    textAlign: 'center'
  },
  loadingWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200
  },
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingTop: 8
  },
  bubbleRow: {
    display: 'flex',
    width: '100%'
  },
  bubble: {
    display: 'inline-block',
    padding: '8px 12px',
    borderRadius: 16,
    maxWidth: '80%'
  },
  assistant: {
    background: '#f1f3f5',
    color: '#111',
    alignSelf: 'flex-start',
    borderTopLeftRadius: 4
  },
  user: {
    background: '#1976d2',
    color: '#fff',
    alignSelf: 'flex-end',
    borderTopRightRadius: 4
  },
  meta: {
    color: '#666'
  }
});

function ComprehensionModal({ open, onClose, conversations = [], loading = false, classes }){
  const [selectedIdx, setSelectedIdx] = useState(null);

  const sorted = useMemo(() => {
    const copy = Array.isArray(conversations) ? [...conversations] : [];
    copy.sort((a, b) => {
      const aT = a && a.dateTime ? new Date(a.dateTime).getTime() : -Infinity;
      const bT = b && b.dateTime ? new Date(b.dateTime).getTime() : -Infinity;
      return bT - aT;
    });
    return copy;
  }, [conversations]);

  const resetAndClose = () => {
    setSelectedIdx(null);
    onClose && onClose();
  };

  const renderList = () => (
    <>
      {loading ? (
        <div className={classes.loadingWrap}>
          <CircularProgress />
        </div>
      ) : sorted.length > 0 ? (
        <List className={classes.list}>
          {sorted.map((conv, idx) => {
            const subtitle = [conv.book || 'Unknown book', conv.textRead].filter(Boolean).join(' • ');
            const dt = conv.dateTime ? new Date(conv.dateTime).toLocaleString() : 'Unknown time';
            return (
              <ListItem key={idx} button onClick={() => setSelectedIdx(idx)}>
                <ListItemText
                  primary={dt}
                  secondary={subtitle}
                />
              </ListItem>
            );
          })}
        </List>
      ) : (
        <div className={classes.emptyState}>
          <Typography variant='h6'>No conversations found</Typography>
        </div>
      )}
    </>
  );

  const renderChat = () => {
    const conv = sorted[selectedIdx] || { messages: [] };
    return (
      <div className={classes.chatContainer}>
        <Typography variant='subtitle2' className={classes.meta}>
          {(conv.book || 'Unknown book') + (conv.textRead ? ` • ${conv.textRead}` : '')}
        </Typography>
        {(conv.messages || []).map((m, i) => {
          const isAssistant = (m.type || '').toLowerCase() === 'assistant';
          const sideClass = isAssistant ? classes.assistant : classes.user;
          const content = (m.message && m.message.trim() !== '') ? m.message : '\u00A0';
          return (
            <div key={i} className={classes.bubbleRow} style={{ justifyContent: isAssistant ? 'flex-start' : 'flex-end' }}>
              <div className={`${classes.bubble} ${sideClass}`}>
                <Typography variant='body2'>{content}</Typography>
              </div>
            </div>
          );
        })}
        {(!conv.messages || conv.messages.length === 0) && (
          <Typography color='textSecondary'>No messages in this conversation</Typography>
        )}
      </div>
    );
  };

  const inListState = selectedIdx == null;

  return (
    <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth='md'>
      <AppBar className={classes.appBar} position='static' color='primary'>
        <Toolbar className={classes.toolbar}>
          <Typography className={classes.headerTitle} variant='h6'>
            {inListState ? 'Reading Comprehension' : 'Conversation'}
          </Typography>
          <IconButton onClick={resetAndClose} size='small' color='inherit' aria-label='close'>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent>
        {inListState ? renderList() : renderChat()}
        {!inListState && (
          <div style={{ marginTop: 12 }}>
            <Typography color='primary' style={{ cursor: 'pointer' }} onClick={() => setSelectedIdx(null)}>Back to all conversations</Typography>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default withStyles(styles)(ComprehensionModal); 